import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Alert, Button, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { HeatStressScoreBadge, mapHeatIndexToCategory } from '../common/heatStress/utils'
import { EmissionTier, MetricState, CanonicalPeriod, PERIODS } from '../common'
import { useHistory } from 'react-router-dom'
import { LoadingValue } from './PortfolioLoading'
import PortfolioKpiValueMotion from './PortfolioKpiValueMotion'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'

function formatNumber(n: number, fraction: number = 1) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: fraction }).format(n)
}

function pickY(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const raw = entry.data?.data?.[tierKey]?.[period]?.y
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN
  return Number.isFinite(n) ? n : null
}

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type PeriodicMetricMap = {
  values: Record<number, Partial<Record<CanonicalPeriod, number | null>>>
  status: Record<number, MetricState | null>
}

const heatStressScorePalette = {
  1: '#c6e8be',
  2: '#f7e4af',
  3: '#f6c8a4',
  4: '#f3a5a5',
  5: '#e8747d',
}

export default function PortfolioHeatStress({
  emissionTier,
  selectedPeriod,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { data: hotDaysByProperty, isLoading: hotDaysLoading } = usePortfolioClimateData('hs:hot_days', {
    groupId: groupId ?? undefined,
  })
  const { data: heatScoresByProperty, isLoading: heatScoresLoading } = usePortfolioClimateData('hs:heat_scores', {
    groupId: groupId ?? undefined,
  })

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'hs', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [])

  const tierKey = emissionTier as unknown as string

  const heatScoreMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (heatScoresByProperty && typeof heatScoresByProperty === 'object') {
      Object.values(heatScoresByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = pickY(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [heatScoresByProperty, tierKey])

  const hotDaysMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (hotDaysByProperty && typeof hotDaysByProperty === 'object') {
      Object.values(hotDaysByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = pickY(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [hotDaysByProperty, tierKey])

  const kpis = useMemo(() => {
    type Agg = {
      avgHeatScore: number | null
      avgHotDays: number | null
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: { avgHeatScore: null, avgHotDays: null },
      [CanonicalPeriod.MID_TERM]: { avgHeatScore: null, avgHotDays: null },
      [CanonicalPeriod.LONG_TERM]: { avgHeatScore: null, avgHotDays: null },
    }

    PERIODS.forEach(period => {
      const collect = (metricValues: Record<number, Partial<Record<CanonicalPeriod, number | null>>>) => {
        if (activePropertyIds) {
          return activePropertyIds
            .map(id => metricValues[id]?.[period] ?? null)
            .filter((v): v is number => typeof v === 'number')
        }
        return Object.values(metricValues)
          .map(v => v[period])
          .filter((v): v is number => typeof v === 'number')
      }

      const heatValues = collect(heatScoreMetric.values)
      const hotDayValues = collect(hotDaysMetric.values)
      const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)

      out[period] = {
        avgHeatScore: mean(heatValues),
        avgHotDays: mean(hotDayValues),
      }
    })

    return out
  }, [activePropertyIds, heatScoreMetric.values, hotDaysMetric.values])

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'heat_score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 150,
        loading: heatScoresLoading,
        getValue: (propertyId, period) => heatScoreMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <HeatStressScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusHeatScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: heatScoresLoading,
          getValue: propertyId => heatScoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'hot_days',
        label: 'Hot Days',
        perPeriod: true,
        width: 140,
        loading: hotDaysLoading,
        format: 'fixed0',
        suffix: 'days/year',
        getValue: (propertyId, period) => hotDaysMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusHotDays',
          label: 'Status (Hot Days)',
          hiddenByDefault: true,
          loading: hotDaysLoading,
          getValue: propertyId => hotDaysMetric.status[propertyId] ?? null,
        },
      },
    ],
    [heatScoreMetric.status, heatScoreMetric.values, heatScoresLoading, hotDaysLoading, hotDaysMetric.status, hotDaysMetric.values],
  )

  const heatScoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'heat_score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: heatScoresLoading,
      getValue: (propertyId, period) => heatScoreMetric.values[propertyId]?.[period] ?? null,
      renderValue: value => <HeatStressScoreBadge score={value} size="dense" />,
    }),
    [heatScoreMetric.values, heatScoresLoading],
  )

  const hotDaysNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'hot_days',
      label: 'Hot Days',
      perPeriod: true,
      loading: hotDaysLoading,
      decimals: 0,
      suffix: 'days/year',
      getValue: (propertyId, period) => hotDaysMetric.values[propertyId]?.[period] ?? null,
    }),
    [hotDaysLoading, hotDaysMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'heat_score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: heatScoreNumericMetric,
        palette: heatStressScorePalette,
        labels: {
          1: 'Low',
          2: 'Moderate',
          3: 'Moderate to High',
          4: 'High',
          5: 'Very High',
        },
        normalize: value => mapHeatIndexToCategory(value) ?? 1,
      },
      {
        key: 'hot_days',
        label: 'Hot Days',
        type: 'continuous',
        metric: hotDaysNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#3b82f6' },
            { offset: 0.5, color: '#f59e0b' },
            { offset: 1, color: '#ef4444' },
          ],
        },
      },
    ],
    [heatScoreNumericMetric, hotDaysNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'hot_days',
        label: 'Hot Days',
        type: 'scaled',
        metric: hotDaysNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [hotDaysNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'heat_index',
        label: 'Heat Index',
        perPeriod: true,
        loading: heatScoresLoading,
        decimals: 1,
        suffix: '°C',
        getValue: (propertyId, period) => heatScoreMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [heatScoreMetric.values, heatScoresLoading],
  )

  const selectedKpi = kpis[selectedPeriod]
  const kpiMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }>
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="Average Risk Rating" transitionKey={kpiMotionKey}>
              <LoadingValue loading={heatScoresLoading} variant="rounded" width={72} height={20}>
                <HeatStressScoreBadge score={selectedKpi.avgHeatScore} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="Average Hot Days" transitionKey={kpiMotionKey}>
              <LoadingValue loading={hotDaysLoading} variant="rounded" width={120} height={24}>
                <Chip label={selectedKpi.avgHotDays != null ? `${formatNumber(selectedKpi.avgHotDays!, 0)} days/year` : '—'} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="heat_score"
          defaultRadiusKey="structure_value"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'heat_score', period: selectedPeriod, sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
